import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface WineLot {
  id: string;
  code: string;
  varietal: string | null;
  vintage: number | null;
  current_volume_l: number | null;
  client_id: string | null;
  org_id: string;
  created_at: string;
  updated_at: string;
  vintrace_batch_id: string | null;
  metadata: any;
}

export interface WineLotWithClient extends WineLot {
  client: {
    id: string;
    name: string;
  } | null;
}

export interface CreateLotInput {
  code: string;
  client_id: string;
  varietal?: string;
  vintage?: number;
  current_volume_l?: number;
}

export interface UpdateLotInput {
  code?: string;
  client_id?: string;
  varietal?: string;
  vintage?: number;
  current_volume_l?: number;
}

export function useWineLots(searchTerm?: string, clientId?: string, limit?: number) {
  return useQuery({
    queryKey: ["wine-lots", searchTerm, clientId, limit],
    queryFn: async () => {
      let query = supabase
        .from("wine_lot")
        .select(`
          *,
          client!wine_lot_client_id_fkey (
            id,
            name
          )
        `)
        .order("created_at", { ascending: false });

      if (searchTerm) {
        query = query.or(`code.ilike.%${searchTerm}%,varietal.ilike.%${searchTerm}%`);
      }

      if (clientId) {
        query = query.eq("client_id", clientId);
      }

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as WineLotWithClient[];
    },
  });
}

export function useCreateLot() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateLotInput) => {
      // First, get org_id from current user
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("wine_lot")
        .insert([{
          code: input.code,
          client_id: input.client_id,
          varietal: input.varietal || null,
          vintage: input.vintage || null,
          current_volume_l: input.current_volume_l || null,
          org_id: userData.org_id,
        } as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wine-lots"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["recent-lots"] });
      toast({
        title: "Lot created",
        description: "The wine lot has been successfully created.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating lot",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateLot() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateLotInput }) => {
      const { data, error } = await supabase
        .from("wine_lot")
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wine-lots"] });
      queryClient.invalidateQueries({ queryKey: ["lot-stats"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["recent-lots"] });
      toast({
        title: "Lot updated",
        description: "The wine lot has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating lot",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteLot() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("wine_lot").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wine-lots"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["recent-lots"] });
      toast({
        title: "Lot deleted",
        description: "The wine lot has been successfully deleted.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting lot",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
